import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { X } from 'lucide-react';
import { archiveLscLeague, restoreLscLeague, LscApiError } from '../api';
import {
  ARCHIVE_LEAGUE_CONFIRMATION_COPY,
  archiveConfirmationMatches,
  isLeagueArchived,
} from '../lifecycle';
import type { LscLeague } from '../types';
import { lscPath } from '../routing';
import ErrorBanner from './ErrorBanner';

interface ArchiveLeagueDangerZoneProps {
  league: LscLeague;
  isOwner: boolean;
  onArchived?: () => void;
  onRestored?: () => void;
}

const ArchiveLeagueDangerZone: React.FC<ArchiveLeagueDangerZoneProps> = ({
  league,
  isOwner,
  onArchived,
  onRestored,
}) => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const archived = isLeagueArchived(league.status);

  if (!isOwner) {
    return null;
  }

  const canConfirmArchive = archiveConfirmationMatches(confirmText, league);

  const handleArchive = async () => {
    if (!canConfirmArchive) return;
    setSubmitting(true);
    setError(null);
    try {
      await archiveLscLeague(league.id, { confirmSlug: league.slug });
      setModalOpen(false);
      setConfirmText('');
      onArchived?.();
      navigate(lscPath('/leagues'));
    } catch (err) {
      const message =
        err instanceof LscApiError ? err.message : 'Could not archive league.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestore = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await restoreLscLeague(league.id);
      onRestored?.();
    } catch (err) {
      const message =
        err instanceof LscApiError ? err.message : 'Could not restore league.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="card border border-red-900/50 space-y-4"
      data-testid="league-archive-danger-zone"
    >
      <h2 className="font-semibold text-red-300">Danger zone</h2>

      {archived ? (
        <div className="space-y-3">
          <p className="text-sm text-secondary-300">
            This league workspace is archived. Restore it to enable imports, syncs, claims, and
            publishing again.
          </p>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleRestore}
            disabled={submitting}
            data-testid="restore-league-button"
          >
            {submitting ? 'Restoring…' : 'Restore league'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-secondary-400">{ARCHIVE_LEAGUE_CONFIRMATION_COPY}</p>
          <button
            type="button"
            className="btn-secondary text-red-300 border-red-800 hover:border-red-600"
            onClick={() => {
              setError(null);
              setConfirmText('');
              setModalOpen(true);
            }}
            data-testid="archive-league-button"
          >
            Archive league
          </button>
        </div>
      )}

      {error && <ErrorBanner message={error} />}

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="archive-league-modal-title"
          data-testid="archive-league-confirm-modal"
        >
          <div className="card max-w-md w-full relative border border-red-900/50">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="absolute top-4 right-4 text-secondary-400 hover:text-white"
              aria-label="Close"
              disabled={submitting}
            >
              <X className="h-5 w-5" />
            </button>
            <h2 id="archive-league-modal-title" className="text-lg font-semibold text-white mb-2">
              Archive league
            </h2>
            <p className="text-sm text-secondary-400 mb-4">{ARCHIVE_LEAGUE_CONFIRMATION_COPY}</p>
            <label className="flex flex-col gap-1 text-sm mb-4">
              <span className="text-secondary-400">
                Type <strong className="text-white">{league.name}</strong> or{' '}
                <strong className="text-white">{league.slug}</strong> to confirm
              </span>
              <input
                type="text"
                className="input-field"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={submitting}
                autoComplete="off"
                data-testid="archive-league-confirm-input"
              />
            </label>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setModalOpen(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-secondary text-red-300 border-red-800"
                onClick={handleArchive}
                disabled={!canConfirmArchive || submitting}
                data-testid="archive-league-confirm-button"
              >
                {submitting ? 'Archiving…' : 'Archive league'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ArchiveLeagueDangerZone;
